import { IExternalCalendar } from '@app-types/websocket/externalCalender.types';
import { commonWebSocket } from '@services/common/common.constants';
import { createReducer } from 'redux-act';
import * as ExternalCalendarActions from "@services/externalCalendar/externalCalendar.actions"
import { EventsByFamily, IExternalCalenderEvent } from '@app-types/api/externalCalendar.types';
import { addDays, isSameDay, toIsoDate } from '@helpers/DateHelper';


const defaultState: {
  externalCalendars: IExternalCalendar[];
  fetchedCalendars: { [calendarId: string]: IExternalCalenderEvent[] }; 
  fetchedEvents: { [day: string]: IExternalCalenderEvent []};
  fetchedEventsByFamily : EventsByFamily;
} = {
  externalCalendars: [],
  fetchedCalendars: {},
  fetchedEvents: {},
  fetchedEventsByFamily: {},
};


const reducer = createReducer<typeof defaultState>({}, defaultState);

// The server can serialize optional fields as null; downstream code dereferences
// url and sharedWith, so make sure they always have the declared shape.
const normalizeCalendar = (calendar: any): IExternalCalendar => {
  return {
    ...calendar,
    url: typeof calendar.url === "string" ? calendar.url : "",
    sharedWith: Array.isArray(calendar.sharedWith) ? calendar.sharedWith : [],
  };
};

const asCalendarArray = (calendars: any): IExternalCalendar[] => {
  return Array.isArray(calendars) ? calendars.filter((calendar) => calendar && typeof calendar === "object") : [];
};

reducer
.on(commonWebSocket.snapshot.EXTERNAL_CALENDAR_SNAPSHOT, (state: typeof defaultState, payload: any) => {
    if (!Array.isArray(payload?.externalCalendars)) {
      return state;
    }

    const previousCalendars = asCalendarArray(state.externalCalendars);
    const calendarsWithFetchDate:IExternalCalendar[] = asCalendarArray(payload.externalCalendars).map((rawCalendar: IExternalCalendar) => {
      const backendCalendar = normalizeCalendar(rawCalendar);
      const existingCalendar = previousCalendars.find(
        (calendar: IExternalCalendar) => calendar.id === backendCalendar.id
      );

      const isNewURL = existingCalendar?.url === backendCalendar.url;
      return {
        ...backendCalendar,
        lastFetchedTime: (existingCalendar?.lastFetchedTime !== undefined && !isNewURL)?existingCalendar.lastFetchedTime :0,
      };
    });

    const updatedFetchedCalendars:{ [calendarId: string]: IExternalCalenderEvent[] } = state.fetchedCalendars?{ ...state.fetchedCalendars }:{};
    const validIds = new Set(calendarsWithFetchDate.map((cal) => (""+cal.id)));

    for (const calId in updatedFetchedCalendars) {
      if (!validIds.has(calId)) {
        delete updatedFetchedCalendars[calId];
      }
    }

    console.log("External calendar snapshot received. Calendars:", calendarsWithFetchDate.length, 
      "Fetched calendars:", Object.keys(updatedFetchedCalendars).length);

    const fetchedEventsByFamily = buildDayDataFromEvents(updatedFetchedCalendars, calendarsWithFetchDate);    
    
    return {
      externalCalendars: calendarsWithFetchDate,
      fetchedCalendars: updatedFetchedCalendars,
      fetchedEvents:{},
      fetchedEventsByFamily
    };
})

.on(commonWebSocket.replace.EXTERNAL_CALENDAR_UPDATE, (state: typeof defaultState, payload: any) => {
    const newCalendarList: IExternalCalendar[] = asCalendarArray(state.externalCalendars);

    if (payload?.externalCalendar && typeof payload.externalCalendar === "object") {
      const updatedCalendar = normalizeCalendar(payload.externalCalendar);
      const indexToUpdate = newCalendarList.findIndex(
        (calendar: any) => calendar.id === updatedCalendar.id
      );

      if (indexToUpdate > -1) {
        const existingCalendar = newCalendarList[indexToUpdate];

        const isNewURL = existingCalendar?.url === updatedCalendar.url;

        newCalendarList[indexToUpdate] = {
          ...updatedCalendar,
          creator:existingCalendar.creator,
          lastFetchedTime:isNewURL?0:existingCalendar.lastFetchedTime,
        };
      } else {
        newCalendarList.push({ ...updatedCalendar, lastFetchedTime: 0 });
      }
    }

    return {
      ...state,
      externalCalendars: newCalendarList,
    };
  })

  .on(commonWebSocket.special.EXTERNAL_CALENDAR_DELETE, (state: typeof defaultState, payload: any) => {
    const updatedFetchedCalendars = { ...state.fetchedCalendars };
    delete updatedFetchedCalendars[""+payload.removeExternalCalendar];
    const updatedCalendars = asCalendarArray(state.externalCalendars).filter((cal) => cal.id !== payload.removeExternalCalendar);

    const fetchedEventsByFamily = buildDayDataFromEvents(updatedFetchedCalendars, updatedCalendars);

    return {
      ...state,
      externalCalendars: updatedCalendars,
      fetchedCalendars: updatedFetchedCalendars,
      fetchedEvents:{},
      fetchedEventsByFamily,
    };
  })


  
  .on(ExternalCalendarActions.saveFetchedEvents, (state: typeof defaultState, payload: any) => {
    const { calendarId } = payload;

    const events: IExternalCalenderEvent[] = Array.isArray(payload?.events)
      ? payload.events.filter((event: any) => event && typeof event === "object")
      : [];
    const updatedCalendars:IExternalCalendar[] = [];

    for (const calendar of asCalendarArray(state.externalCalendars)){ // lets go through all calendars in state and update the correct one
      if (calendar.id === Number(calendarId)) { // found the calendar!
        const fixedCal:IExternalCalendar = { ...calendar, lastFetchedTime:Date.now() } ; // lets update the last fetched time and create a  new object
        const calendarURL = typeof calendar.url === "string" ? calendar.url : "";
        for (const event of events) { // lets go though the data that we got
          event.tagged = calendar.calendarPerson; // lets add tag data so we get color set on the event

          const shared:boolean = Array.isArray(calendar.sharedWith) && calendar.sharedWith.length > 0;
          let internal:boolean = false;

          if (calendarURL.includes("https://calendar.varannanvecka.app")) {
            internal = true;
          }

          event.internal = internal; 
          event.shared = shared;

          const startDate = new Date(event.startTime);
          if (startDate.getHours() === 0 && startDate.getMinutes() === 0 && startDate.getSeconds() === 0) {
            if (event.endTime - event.startTime < 60000*30) {
              event.endTime = (addDays(startDate, 1)).getTime();
            }
          }

        }
        updatedCalendars.push(fixedCal);
      } else { 
        updatedCalendars.push(calendar);
      }
    }

    const updatedFetchedCalendars = {
      ...state.fetchedCalendars,
      ["" + calendarId]: events, 
    };

    console.log("External calendar events saved. Calendars:", updatedCalendars.length, 
      "Fetched calendars:", Object.keys(updatedFetchedCalendars).length);

    const fetchedEventsByFamily = buildDayDataFromEvents(updatedFetchedCalendars, updatedCalendars);    
    
    return {
      externalCalendars: updatedCalendars,
      fetchedCalendars: updatedFetchedCalendars,
      fetchedEvents:{},
      fetchedEventsByFamily,
    };
  });
  
  function buildDayDataFromEvents(calendars: {[calendarId: string]: IExternalCalenderEvent[];},externalCalendars:IExternalCalendar[]):EventsByFamily{
    // const data:{[calendarId: string]: IExternalCalenderEvent[];} = {};

    const familyData:EventsByFamily = {};

    for (const calendarId of Object.keys(calendars)) {

      const extCal = externalCalendars.find((c) => (""+c.id) === calendarId);
      if (extCal === undefined) {continue;}
      const familyId = extCal.familyId;

      // Ensure an EventsByDay map exists for this family
      if (familyData[""+familyId] === undefined) {
        familyData[""+familyId] = {};
      }
      const dataForFamily = familyData[""+familyId];

      const events = calendars[calendarId];
      if (events != null){
        for (const event of events){
          
          // every day the event covers, its last day too (at most 100). The end is exclusive, so an event
          // ending at midnight, like an all-day one, stays off the next day.
          const lastInstant = event.endTime > event.startTime ? event.endTime - 1 : event.startTime;
          const lastIsoDate = toIsoDate(new Date(lastInstant));
          let day = new Date(event.startTime);
          let isoDate = toIsoDate(day);
          for (let loops = 0; loops < 100; loops++) {
            let eventList = dataForFamily[isoDate];
            if (eventList === undefined){
              eventList = [];
              dataForFamily[isoDate] = eventList;
            }
            eventList.push(event);
            if (isoDate === lastIsoDate) { break; }
            day = addDays(day, 1);
            isoDate = toIsoDate(day);
          }
        }
      }
    }

    return familyData;
  }

export default reducer;



// WEBPACK FOOTER //
// ./src/services/externalCalendar/externalCalendar.reducer.ts